import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import _toNumber from 'lodash/toNumber';
import React, { useCallback, useEffect, useState } from 'react';
import {
  getUnauthedSearchBrandClubs,
  ProductOperation,
  searchBrandClubsForUserV2,
  UNAUTHENTICATED_SEARCH_PRODUCTS,
} from '../../api/graphQl';
import { Button } from '../../Components';
import { BrandClub, ProductRewardSearchResult } from '../../types';
import { useJoinBrandClubWithRedirect } from '../../utils/hooks/useJoinBrandClub';
import { FilterDialog } from '../BrandclubNavigation/FilterDialog';
import ProductBrandCard from '../components/BrandclubProductCard/ProductBrandCard';
import {
  DESKTOP_HORIZONTAL_PADDING,
  DESKTOP_SCREEN_SIZE,
  MOBILE_HORIZONTAL_PADDING,
  MOBILE_SCREEN_SIZE,
  TABLET_HORIZONTAL_PADDING,
  TABLET_SCREEN_SIZE,
  WIDGET_SPACE,
  WIDGET_SPACE_HALF,
} from '../../utils/constants';
import { GenericContainer } from '../components/GenericContainer';
import { BaseComponentPropsType, StoreBrandingType } from '../type';
import SearchProductGrid from './SearchProductGrid';
import { searchParamsToSearchFilters } from './searchResultsUtils';

const PAGE_SIZE = 60;
const SearchResultsContainer = styled(GenericContainer)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: 30,
  paddingBottom: WIDGET_SPACE,
  paddingLeft: '5%',
  paddingRight: '5%',
  boxSizing: 'border-box',
  color: theme.palette.primary.main,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    backgroundColor: '#fff',
    padding: `${WIDGET_SPACE_HALF}px ${0}px`,
  },
}));

const ContentWrapper = styled(Grid)({
  maxWidth: DESKTOP_SCREEN_SIZE - DESKTOP_HORIZONTAL_PADDING * 2,
  width: '100%',
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    padding: `${0}px ${TABLET_HORIZONTAL_PADDING}px`,
  },
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    padding: `${0}px ${MOBILE_HORIZONTAL_PADDING}px`,
  },
});

const findMostCommonBrandId = (searchProducts: ProductRewardSearchResult[]): number | undefined => {
  const brandCounts = new Map<number, number>();
  let mostCommonBrandId: number | undefined = undefined;
  let maxCount = 0;

  for (const product of searchProducts) {
    const count = (brandCounts.get(product.brandId) || 0) + 1;
    brandCounts.set(product.brandId, count);
    if (count >= maxCount) {
      maxCount = count;
      mostCommonBrandId = product.brandId;
    }
  }

  return mostCommonBrandId;
};

const SearchHeader = styled(Grid)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  color: theme.palette.primary.main,
  marginBottom: 38,
  marginTop: 30,
  '.headerText': {
    fontSize: 16,
    fontWeight: 600,
  },
}));

const PaginationContainer = styled('div')({
  textAlign: 'right',
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: 20,
  },
});

interface SearchResultsProps extends Omit<BaseComponentPropsType, 'site' | 'page'> {
  onShareClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, product?: ProductRewardSearchResult) => void;
  products: string[];
  site: BaseComponentPropsType['site'] | undefined;
}

export const SearchResults = ({
  LinkComponent,
  apolloClient,
  useSearchParams,
  pageContext,
  onShareClick,
  handleJoinClub,
  useNavigate,
  appConfig,
  ...props
}: SearchResultsProps) => {
  const [popularProducts, setPopularProducts] = useState<ProductRewardSearchResult[]>([]);
  const navigate = useNavigate();
  const locationPathname = window.location.pathname;
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [totalResultCount, setTotalResultCount] = useState(0);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [mostPopularBrand, setMostPopularBrand] = useState<BrandClub | undefined>(undefined);
  const searchRequestBody = searchParamsToSearchFilters(searchParams, pageContext);
  const searchTerms = searchRequestBody?.titleInclude?.join(' ');
  const searchText = searchTerms ?? 'All products';
  const [searchInputText, setSearchInputText] = useState(searchTerms);
  const isSignedIn = !!props.userProfile;

  const { joinedState, handleJoin, campaignRewards } = useJoinBrandClubWithRedirect({
    brand: mostPopularBrand,
    handleJoinClub: handleJoinClub,
    navigate,
  });

  const fetchBrandclub = useCallback(
    async (brandIds: number[] = [], searchTerm?: string) => {
      let titleInclude: string[] | undefined = undefined;
      if (searchTerm && !brandIds.length) {
        titleInclude = [searchTerm];
      }
      try {
        const RES_KEY = isSignedIn ? 'SearchBrandClubsForUserV2' : 'UnauthedSearchBrandClubs';
        const query = isSignedIn ? searchBrandClubsForUserV2 : getUnauthedSearchBrandClubs;
        const res = await apolloClient.query<{ [k in typeof RES_KEY]: { items?: BrandClub[] } }>({
          query,
          variables: {
            page: {
              start: 0,
              size: 1,
            },
            titleInclude,
            brandIds,
            searchIntent: 'InAppSearch',
          },
          fetchPolicy: 'network-only',
        });
        setMostPopularBrand(res?.data?.[RES_KEY]?.items?.[0]);
      } catch (e) {
        console.error(e);
        return undefined;
      }
    },
    [isSignedIn],
  );

  useEffect(() => {
    // Update the search input text when the search terms change
    setSearchInputText(searchTerms);
  }, [searchTerms]);

  useEffect(() => {
    // close the filter dialog when the location changes
    setFilterDialogOpen(false);
  }, [locationPathname]);

  const page: number = _toNumber(searchParams.get('page'));

  const movePrev = () => {
    searchParams.set('page', `${page - PAGE_SIZE}`);
    window.scrollTo({ top: 0, behavior: 'auto' });
    setSearchParams(searchParams);
  };
  const moveNext = () => {
    searchParams.set('page', `${page + PAGE_SIZE}`);
    window.scrollTo({ top: 0, behavior: 'auto' });
    setSearchParams(searchParams);
  };

  useEffect(() => {
    if (apolloClient) {
      const fetchProducts = async () => {
        setLoading(true);
        const treatment =
          pageContext?.abTestingTreatments && pageContext?.abTestingTreatments.searchImprovementTreatment
            ? {
                search: pageContext?.abTestingTreatments.searchImprovementTreatment,
              }
            : undefined;
        const { data } = await apolloClient.query({
          query: UNAUTHENTICATED_SEARCH_PRODUCTS,
          variables: {
            ...searchRequestBody,
            page: {
              start: page,
              size: PAGE_SIZE,
            },
            computeFacets: true,
            facetFieldNames: ['categoryId', 'retailerId', 'subCategoryId', 'brandId'],
            treatment,
          },
        });
        // only fetch the brandclub if we are on the first page and the store branding is universal
        if (page === 0 && appConfig?.domainConfig?.storeBrandingType === StoreBrandingType.UniversalStore) {
          const mostPopularBrandId = findMostCommonBrandId(
            // The brandclub app only fetches 20 products at a time and only use those 20 to calculate the most popular brand.
            // To ensure we display the same results even though we fetch more products, we only use the first 20 products and only on the first page.
            // See https://gitlab.com/stackline-dev/brandclub-mobile-app/-/blob/5e55fd3d9da2211dbb7194745e8aa87da9e4606c/src/screens/Shop/SearchResultsPage.tsx#L203 for reference
            data?.[ProductOperation.UnauthenticatedSearch]?.products?.slice(0, 20),
          );
          await fetchBrandclub(mostPopularBrandId != null ? [mostPopularBrandId] : undefined, searchTerms);
        }

        setTotalResultCount(data?.[ProductOperation.UnauthenticatedSearch]?.totalProducts ?? 0);
        setPopularProducts(data?.[ProductOperation.UnauthenticatedSearch]?.products ?? []);
        setLoading(false);
      };
      fetchProducts();
    }
  }, [apolloClient, appConfig, searchParams, fetchBrandclub]);

  const hasResults = !loading && popularProducts.length;

  const disableBack = page === 0;
  const totalPages = Math.ceil(totalResultCount / PAGE_SIZE);
  const disableNext = page + 1 >= totalPages;

  return (
    <SearchResultsContainer>
      <ContentWrapper>
        <FilterDialog
          searchInput={searchInputText}
          setSearchInput={setSearchInputText}
          brandId={props.brandEntity?.id}
          routePrefix={props.routePrefix}
          navigate={navigate}
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          persistentQueryParams={props.persistentQueryParams}
          searchParams={searchParams}
          pageContext={pageContext}
          axios={props.axios}
        />
        <SearchHeader container wrap="nowrap" gap={1}>
          {loading ? (
            <div className="headerText">
              {' '}
              <Typography
                component="h2"
                fontSize={24}
                fontWeight="bold"
                display="inline-flex"
                alignItems="center"
                overflow="hidden"
                whiteSpace="nowrap"
                sx={{
                  [`@media screen and (max-width:  390px)`]: {
                    display: 'inline',
                    textOverflow: 'ellipsis',
                  },
                }}
              >
                Searching...
              </Typography>
            </div>
          ) : hasResults ? (
            <>
              <Typography
                component="h2"
                fontSize={24}
                fontWeight="bold"
                display="inline-flex"
                alignItems="center"
                overflow="hidden"
                whiteSpace="nowrap"
                sx={{
                  [`@media screen and (max-width:  390px)`]: {
                    display: 'inline',
                    textOverflow: 'ellipsis',
                  },
                }}
              >
                Search results for “
                <Box component="span" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  {searchText}
                </Box>
                ”
              </Typography>
              <Button sx={{ height: 'auto', whiteSpace: 'nowrap' }} onClick={() => setFilterDialogOpen(true)}>
                Filter
              </Button>
            </>
          ) : (
            <div className="headerText">
              No search results were returned for “{searchText}”. Please try another query.
            </div>
          )}
        </SearchHeader>
        <SearchProductGrid
          onShareClick={onShareClick}
          loading={loading}
          LinkComponent={LinkComponent}
          products={popularProducts}
        >
          {mostPopularBrand && (
            <ProductBrandCard
              brand={mostPopularBrand}
              joinedState={joinedState}
              reward={campaignRewards[0]?.rewardAmount || 0}
              joined={mostPopularBrand.isMember}
              onJoin={handleJoin}
              LinkComponent={LinkComponent}
            />
          )}
        </SearchProductGrid>
        {hasResults && (!disableBack || !disableNext) ? (
          <PaginationContainer>
            <IconButton disableRipple disableFocusRipple onClick={movePrev} disabled={disableBack}>
              <ChevronLeftIcon
                sx={(theme) => ({ color: theme.palette.primary.main, opacity: disableBack ? 0.5 : 1 })}
              />
            </IconButton>
            <IconButton disableRipple disableFocusRipple onClick={moveNext} disabled={disableNext}>
              <ChevronRightIcon
                sx={(theme) => ({ color: theme.palette.primary.main, opacity: disableNext ? 0.5 : 1 })}
              />
            </IconButton>
          </PaginationContainer>
        ) : null}
      </ContentWrapper>
    </SearchResultsContainer>
  );
};
